import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import {
  centersAPI,
  serviceTypesAPI,
  servicesAPI,
  otpAPI,
  timeSlotsAPI,
  appointmentsAPI
} from '../utils/api';

const AppointmentBooking = () => {
  const { t } = useTranslation();
  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm();
  
  const [centers, setCenters] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [confirmation1, setConfirmation1] = useState(false);
  const [confirmation2, setConfirmation2] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  
  // Predefined centers
  const predefinedCenters = [
    { id: 'city1', name: 'City 1' },
    { id: 'city2', name: 'City 2' },
    { id: 'city3', name: 'City 3' }
  ];
  
  // Service type options
  const serviceTypeOptions = [
    { id: 'passport', name: 'Passport' },
    { id: 'visa', name: 'Visa' }
  ];
  
  // Services based on type
  const passportServices = [
    'Passport Requirements',
    'Global Entry Program',
    'Passport Fee',
    'Photograph Specifications',
    'Other Application Forms',
    'Tatkal Service'
  ];
  
  const visaServices = [
    'General Information',
    'Tourist Visa',
    'Business Visa',
    'Employment Visa',
    'Student Visa',
    'Conference Visa',
    'Journalist Visa',
    'Film Visa',
    'Transit Visa',
    'Entry Visa',
    'Medical Visa',
    'Transfer of Visa'
  ];
  
  const [availableServices, setAvailableServices] = useState([]);
  
  const selectedCenter = watch('center_id');
  const selectedServiceType = watch('service_type');
  const email = watch('email');

  // Load time slots when service type changes
  useEffect(() => {
    if (selectedServiceType === 'passport') {
      setAvailableServices(passportServices);
    } else if (selectedServiceType === 'visa') {
      setAvailableServices(visaServices);
    } else {
      setAvailableServices([]);
    }
    setValue('service_name', ''); // Reset service name when type changes
  }, [selectedServiceType, setValue]);

  // Load time slots when center and date change
  useEffect(() => {
    if (selectedCenter && selectedDate) {
      loadTimeSlots(selectedCenter, format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedCenter, selectedDate]);

  const loadTimeSlots = async (centerId, date) => {
    try {
      const response = await timeSlotsAPI.getAvailable(centerId, date);
      setTimeSlots(response.data);
    } catch (error) {
      toast.error('Failed to load time slots');
    }
  };

  const handleSendOTP = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await otpAPI.send(email);
      setOtpSent(true);
      toast.success(t('otpSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    const otp = watch('otp');
    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }
    
    setLoading(true);
    try {
      await otpAPI.verify({ email, otp });
      setOtpVerified(true);
      toast.success(t('otpVerified'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    if (!otpVerified) {
      toast.error('Please verify your email with OTP first');
      return;
    }

    if (!confirmation1 || !confirmation2) {
      toast.error('Please accept both confirmations');
      return;
    }

    setLoading(true);
    try {
      const appointmentData = {
        center_id: data.center_id || selectedCenter,
        appointment_date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: data.time_slot,
        applicant_name: data.applicant_name,
        isd_code: data.isd_code,
        mobile_number: data.mobile_number,
        email: data.email,
        otp: data.otp,
        service_type_id: data.service_type,
        service_id: data.service_name,
        passport_number: data.passport_number,
        service_description: data.service_description || ''
      };
      
      const response = await appointmentsAPI.create(appointmentData);
      setReferenceId(response.data.reference_id);
      setBookingSuccess(true);
      toast.success(t('appointmentBooked'));
      
      // Reset form
      reset();
      setOtpSent(false);
      setOtpVerified(false);
      setSelectedDate(null);
      setConfirmation1(false);
      setConfirmation2(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    reset();
    setOtpSent(false);
    setOtpVerified(false);
    setSelectedDate(null);
    setConfirmation1(false);
    setConfirmation2(false);
    setBookingSuccess(false);
  };

  if (bookingSuccess) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center py-16 px-4">
          <div className="max-w-md w-full text-center">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-primary mb-4">
              {t('success')}
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              {t('appointmentBooked')}
            </p>
            <div className="bg-secondary p-6 rounded-lg mb-6">
              <p className="text-sm text-muted-foreground mb-2">{t('referenceId')}</p>
              <p className="text-2xl font-mono font-bold text-accent">{referenceId}</p>
            </div>
            <Button onClick={() => setBookingSuccess(false)} className="w-full" data-testid="book-another-btn">
              {t('bookAppointment')}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-secondary">
      <Header />
      
      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
              {t('appointmentTitle')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('heroSubtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 md:p-8">
            <div className="space-y-6">
              {/* Center Selection */}
              <div>
                <Label htmlFor="center_id">{t('selectCenter')} *</Label>
                <Select
                  onValueChange={(value) => setValue('center_id', value)}
                  data-testid="center-select"
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder={t('selectCenter')} />
                  </SelectTrigger>
                  <SelectContent>
                    {predefinedCenters.map((center) => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Service Type Selection */}
              <div>
                <Label htmlFor="service_type">{t('serviceType')} *</Label>
                <Select
                  onValueChange={(value) => setValue('service_type', value)}
                  data-testid="service-type-select"
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder={t('serviceType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypeOptions.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Service Name Selection */}
              {availableServices.length > 0 && (
                <div>
                  <Label htmlFor="service_name">{t('serviceName')} *</Label>
                  <Select
                    onValueChange={(value) => setValue('service_name', value)}
                    data-testid="service-name-select"
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder={t('serviceName')} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableServices.map((service, index) => (
                        <SelectItem key={index} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Service Description */}
              <div>
                <Label htmlFor="service_description">{t('serviceDescription')}</Label>
                <Textarea
                  id="service_description"
                  {...register('service_description')}
                  rows={4}
                  placeholder="Please describe your service requirements..."
                  data-testid="service-description-input"
                />
              </div>

              {/* Date Selection */}
              <div>
                <Label>{t('appointmentDate')} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-start text-left font-normal"
                      data-testid="date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP') : t('appointmentDate')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time Slot Selection */}
              {timeSlots.length > 0 && (
                <div>
                  <Label htmlFor="time_slot">{t('timeSlot')} *</Label>
                  <Select
                    onValueChange={(value) => setValue('time_slot', value)}
                    data-testid="timeslot-select"
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder={t('timeSlot')} />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem
                          key={slot.time}
                          value={slot.time}
                          disabled={!slot.available}
                        >
                          {slot.time} {!slot.available && '(Booked)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Applicant Name */}
              <div>
                <Label htmlFor="applicant_name">{t('applicantName')} *</Label>
                <Input
                  id="applicant_name"
                  {...register('applicant_name', { required: true })}
                  className="h-12"
                  data-testid="applicant-name-input"
                />
                {errors.applicant_name && (
                  <p className="text-red-500 text-sm mt-1">This field is required</p>
                )}
              </div>

              {/* ISD Code and Mobile */}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-1">
                  <Label htmlFor="isd_code">{t('isdCode')} *</Label>
                  <Input
                    id="isd_code"
                    {...register('isd_code', { required: true })}
                    placeholder="+82"
                    className="h-12"
                    data-testid="isd-code-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="mobile_number">{t('mobileNumber')} *</Label>
                  <Input
                    id="mobile_number"
                    {...register('mobile_number', { required: true })}
                    className="h-12"
                    data-testid="mobile-number-input"
                  />
                </div>
              </div>

              {/* Email with OTP */}
              <div>
                <Label htmlFor="email">{t('email')} *</Label>
                <div className="flex gap-2">
                  <Input
                    id="email"
                    type="email"
                    {...register('email', { required: true })}
                    className="h-12"
                    disabled={otpVerified}
                    data-testid="email-input"
                  />
                  <Button
                    type="button"
                    onClick={handleSendOTP}
                    disabled={loading || otpVerified}
                    className="whitespace-nowrap"
                    data-testid="send-otp-btn"
                  >
                    {t('sendOTP')}
                  </Button>
                </div>
              </div>

              {/* OTP Input */}
              {otpSent && !otpVerified && (
                <div>
                  <Label htmlFor="otp">{t('otpCode')} *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="otp"
                      {...register('otp')}
                      className="h-12 font-mono"
                      placeholder="123456"
                      maxLength={6}
                      data-testid="otp-input"
                    />
                    <Button
                      type="button"
                      onClick={handleVerifyOTP}
                      disabled={loading}
                      data-testid="verify-otp-btn"
                    >
                      {t('verifyOTP')}
                    </Button>
                  </div>
                </div>
              )}

              {otpVerified && (
                <>
                  {/* Passport Number */}
                  <div>
                    <Label htmlFor="passport_number">{t('passportNumber')} *</Label>
                    <Input
                      id="passport_number"
                      {...register('passport_number', { required: true })}
                      className="h-12 font-mono"
                      placeholder="A12345678"
                      data-testid="passport-number-input"
                    />
                  </div>

                  {/* Confirmations */}
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="confirm1"
                        checked={confirmation1}
                        onCheckedChange={setConfirmation1}
                        data-testid="confirmation-1"
                        className="mt-1"
                      />
                      <Label htmlFor="confirm1" className="text-sm cursor-pointer leading-relaxed">
                        I have reviewed the checklist of required documents and have all documents/fees ready.
                      </Label>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="confirm2"
                        checked={confirmation2}
                        onCheckedChange={setConfirmation2}
                        data-testid="confirmation-2"
                        className="mt-1"
                      />
                      <Label htmlFor="confirm2" className="text-sm cursor-pointer leading-relaxed">
                        I understand that failure to fulfill any requirement may result in my application not being accepted.
                      </Label>
                    </div>
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  className="flex-1 h-12"
                  data-testid="reset-btn"
                >
                  {t('reset')}
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !otpVerified || !confirmation1 || !confirmation2}
                  className="flex-1 h-12 bg-accent hover:bg-accent/90"
                  data-testid="submit-appointment-btn"
                >
                  {loading ? t('pleaseWait') : t('submit')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AppointmentBooking;
